'use client';

import { useState, useEffect } from 'react';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { getUserProfileWithRole } from '@/lib/userManagement';
import { normalizeUserProfile } from '@/lib/row-utils';

export interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  display_name?: string;
  preferred_name?: string;
  avatar_url?: string;
  username?: string;
  bio?: string;
  location?: string;
  experience_level?: string;
  specialties?: string[];
  tags?: string[];
  account_type?: string;
  onboarding_completed?: boolean;
  public_profile?: boolean;
  created_at: string;
  updated_at: string;
}

export function useUserProfile() {
  const { user } = useAuthContext();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        setProfile(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await getUserProfileWithRole(user.id);
        
        if (error) {
          console.error('Error fetching user profile:', error);
          setError(error.message);
          setProfile(null);
        } else {
          // Extract just the profile data from the combined user/profile response and normalize it
          if (data?.profile) {
            const normalizedProfile = normalizeUserProfile(data.profile);
            if (normalizedProfile) {
              setProfile(normalizedProfile);
            }
          } else {
            setProfile(null);
          }
        }
      } catch (err) {
        console.error('Error in useUserProfile:', err);
        setError('Failed to fetch profile');
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user?.id]);

  const hasProfile = !!profile;
  const hasCompletedOnboarding = profile?.onboarding_completed === true;
  const hasDisplayName = !!(profile?.full_name || profile?.preferred_name || profile?.username);
  const isProfileComplete = hasProfile && hasCompletedOnboarding && hasDisplayName;

  return {
    profile,
    loading,
    error,
    hasProfile,
    hasCompletedOnboarding,
    hasDisplayName,
    isProfileComplete,
  };
} 